import React, { useState } from "react";
import Select from "react-select";
import { FaExclamationTriangle } from "react-icons/fa";
import Button from "components/ui/Button";
import { showToast } from "components/ui/toast";
import {
  useGetSocialSubscribersQuery,
  useSendSocialReminderMutation,
} from "apis/socialPost";
import { useUpdateUserMutation, useGetUserQuery } from "apis/user";
import OrganizationFilter from "components/organization/OrganizationFilter";
import useDebounce from "hooks/useDebounce";



const PlatformBadge = ({ platform }) => (
  <span className="inline-block bg-lightgray border border-mediumgray text-darkergray text-xs font-medium px-2 py-0.5 rounded capitalize">
    {platform}
  </span>
);

const WarningBadge = ({ text }) => (
  <span className="inline-flex items-center gap-1 bg-red-50 border border-red-300 text-red-600 text-xs font-medium px-2 py-0.5 rounded">
    <FaExclamationTriangle fontSize={10} />
    {text}
  </span>
);


const SubscriberCard = ({
  subscriber,
  allUsers,
  onSendReminder,
  onUpdateCollaborators,
  sendingFor,
  updatingFor,
}) => {
  const { user, isActive, status } = subscriber;
  const collaborators = user.collaborators || [];
  const platforms = user.platforms || [];

  const bothPlatformsMissing =
    !platforms.includes("facebook") && !platforms.includes("instagram");
  const collabsMissingIg = collaborators.filter((c) => !c.instagramUsername);

  // Collaborators are org-bound. A subscriber with no organization can be
  // offered nobody — two unresolved orgs are not a match.
  const subscriberOrganizationId = subscriber.organization?._id?.toString();
  const availableToAdd = allUsers.filter(
    (u) =>
      u.value !== subscriber.userId.toString() &&
      !collaborators.some(
        (c) => (c._id || c).toString() === u.value.toString()
      ) &&
      subscriberOrganizationId &&
      u.organizationId === subscriberOrganizationId
  );

  const { mutateAsync: updateUser } = useUpdateUserMutation();

  // Local state for instagram username inputs (keyed by collaborator id)
  const [igInputs, setIgInputs] = useState({});
  const [savingIg, setSavingIg] = useState({});
  const [editingIg, setEditingIg] = useState({});

  const startEditIg = (collab) => {
    const cId = (collab._id || collab).toString();
    setIgInputs((s) => ({ ...s, [cId]: collab.instagramUsername || "" }));
    setEditingIg((s) => ({ ...s, [cId]: true }));
  };

  const cancelEditIg = (cId) => {
    setEditingIg((s) => ({ ...s, [cId]: false }));
    setIgInputs((s) => ({ ...s, [cId]: "" }));
  };

  const handleIgSave = async (collab) => {
    const cId = (collab._id || collab).toString();
    const value = (igInputs[cId] || "").trim().replace(/^@/, "");
    if (!value) return;
    setSavingIg((s) => ({ ...s, [cId]: true }));
    try {
      await updateUser({ id: cId, name: collab.name, instagramUsername: value });
      setIgInputs((s) => ({ ...s, [cId]: "" }));
      setEditingIg((s) => ({ ...s, [cId]: false }));
      onUpdateCollaborators(); // trigger refetch
    } catch (err) {
      showToast("error", err?.response?.data?.message || "Failed to save");
    } finally {
      setSavingIg((s) => ({ ...s, [cId]: false }));
    }
  };

  return (
    <div
      className={`border rounded-lg p-4 space-y-3 ${
        !isActive ? "border-red-300 bg-red-50" : "border-mediumgray bg-white"
      }`}
    >
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-darkergray">{user.name || "Unknown"}</p>
          <p className="text-sm text-darkgray">{user.email}</p>
          <p className="text-sm text-darkgray">
            {subscriber.organization?.name || "No organization"}
          </p>
          {user.instagramUsername && (
            <p className="text-sm text-darkgray">{user.instagramUsername}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-1 items-center">
          {!isActive && (
            <WarningBadge
              text={
                subscriber.companyCoverage
                  ? `Office subscription ${subscriber.companyCoverage.status}`
                  : `Subscription ${status}`
              }
            />
          )}
          {isActive && bothPlatformsMissing && (
            <WarningBadge text="No platforms connected" />
          )}
          {isActive && collabsMissingIg.length > 0 && (
            <WarningBadge
              text={`${collabsMissingIg.length} collaborator${
                collabsMissingIg.length > 1 ? "s" : ""
              } missing Instagram`}
            />
          )}
        </div>
      </div>

      {/* Platforms */}
      <div>
        <p className="text-xs text-darkgray uppercase font-semibold mb-1">
          Platforms
        </p>
        {platforms.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {platforms.map((p) => (
              <PlatformBadge key={p} platform={p} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-darkgray italic">None connected</p>
        )}
      </div>

      {/* Collaborators — only shown for active subscribers */}
      {isActive && (
        <div>
          <p className="text-xs text-darkgray uppercase font-semibold mb-1">
            Collaborators
          </p>
          <div className="space-y-2 mb-2">
            {collaborators.length === 0 && (
              <p className="text-sm text-darkgray italic">None</p>
            )}
            {collaborators.map((c) => {
              const cId = (c._id || c).toString();
              const missingIg = !c.instagramUsername;
              return (
                <div
                  key={cId}
                  className={`rounded px-2 py-1.5 ${
                    missingIg
                      ? "bg-red-50 border border-red-200"
                      : "bg-lightgray"
                  }`}
                >
                  <div className="flex items-center justify-between text-sm">
                    <span>
                      {c.name || c.email || cId}
                      {c.instagramUsername && !editingIg[cId] ? (
                        <span className="text-darkgray ml-1">
                          {c.instagramUsername}
                        </span>
                      ) : !c.instagramUsername && !editingIg[cId] ? (
                        <span className="text-red-500 ml-1 text-xs">
                          (no Instagram username)
                        </span>
                      ) : null}
                    </span>
                    <div className="flex items-center gap-2 ml-2">
                      {!editingIg[cId] && (
                        <button
                          onClick={() => startEditIg(c)}
                          className="text-darkgray hover:text-black text-xs"
                        >
                          {c.instagramUsername ? "Edit" : "Add"}
                        </button>
                      )}
                      <button
                        onClick={() =>
                          onUpdateCollaborators(subscriber, "remove", cId)
                        }
                        disabled={updatingFor === subscriber.userId.toString()}
                        className="text-darkgray hover:text-red-500 text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  {/* Instagram username input — shown when missing or editing */}
                  {(missingIg || editingIg[cId]) && (
                    <div className="flex gap-1 mt-1.5">
                      <input
                        type="text"
                        value={igInputs[cId] || ""}
                        onChange={(e) =>
                          setIgInputs((s) => ({ ...s, [cId]: e.target.value }))
                        }
                        placeholder="Enter correct Instagram username (without @)"
                        className="form-input flex-1 border border-mediumgray"
                      />
                      <Button
                        onClick={() => handleIgSave(c)}
                        loading={savingIg[cId]}
                        className="black-button text-xs"
                        disabled={!igInputs[cId]?.trim()}
                      >
                        Save
                      </Button>
                      {editingIg[cId] && (
                        <button
                          onClick={() => cancelEditIg(cId)}
                          className="text-darkgray hover:text-black text-xs px-1"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Add collaborator */}
          <Select
            options={availableToAdd}
            value={null}
            onChange={(selected) =>
              selected && onUpdateCollaborators(subscriber, "add", selected)
            }
            placeholder={
              subscriberOrganizationId
                ? `Add collaborator from ${subscriber.organization.name}...`
                : "No organization — collaborators unavailable"
            }
            isSearchable
            isDisabled={!subscriberOrganizationId}
            isLoading={updatingFor === subscriber.userId.toString()}
            classNamePrefix="react-select"
            noOptionsMessage={() =>
              subscriberOrganizationId
                ? "No users in this organization"
                : "This subscriber has no organization"
            }
          />
        </div>
      )}

      {/* Expired renewal reminder */}
      {!isActive && (
        <Button
          onClick={() =>
            onSendReminder(subscriber.userId.toString(), "expired")
          }
          loading={sendingFor === subscriber.userId.toString() + "expired"}
          className="black-button text-sm"
        >
          Send Renewal Reminder
        </Button>
      )}

      {/* Platform reminder for active but both platforms missing */}
      {isActive && bothPlatformsMissing && (
        <Button
          onClick={() =>
            onSendReminder(subscriber.userId.toString(), "platform")
          }
          loading={sendingFor === subscriber.userId.toString() + "platform"}
          className="black-button text-sm"
        >
          Send Platform Reminder
        </Button>
      )}
    </div>
  );
};

const SocialSubscribers = () => {
  const [organizationId, setOrganizationId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 300);

  const {
    data: subscribers,
    isPending,
    refetch,
  } = useGetSocialSubscribersQuery({
    ...(organizationId ? { organizationId } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
  });
  const { mutate: sendReminder } = useSendSocialReminderMutation();
  const { mutateAsync: updateUser } = useUpdateUserMutation();
  const { data: usersData } = useGetUserQuery();

  const allUsers = usersData
    .filter((u) => !u.isDeleted)
    .map((u) => ({
      value: u._id,
      label: u.name || u.email || u._id,
      instagramUsername: u.instagramUsername,
      organizationId: u.organization?._id?.toString() || null,
    }));

  const [sendingFor, setSendingFor] = useState(null);
  const [updatingFor, setUpdatingFor] = useState(null);

  const activeSubscribers = subscribers.filter((s) => s.isActive);
  const expiredSubscribers = subscribers.filter((s) => !s.isActive);

  const handleSendReminder = (userId, type) => {
    setSendingFor(userId + type);
    sendReminder({ userId, type }, { onSettled: () => setSendingFor(null) });
  };

  const handleUpdateCollaborators = async (subscriber, action, payload) => {
    // payload: "add" -> react-select option; "remove" -> collabId string; undefined -> just refetch
    if (!action) {
      refetch();
      return;
    }
    const existingIds = (subscriber.user.collaborators || []).map((c) =>
      (c._id || c).toString()
    );
    let newCollaborators;
    if (action === "add") {
      if (existingIds.includes(payload.value.toString())) return;
      newCollaborators = [...existingIds, payload.value];
    } else {
      newCollaborators = existingIds.filter((id) => id !== payload);
    }
    setUpdatingFor(subscriber.userId.toString());
    try {
      await updateUser({
        id: subscriber.userId,
        name: subscriber.user.name,
        collaborators: newCollaborators,
      });
      refetch();
    } catch (err) {
      showToast("error", err?.response?.data?.message || "Failed to update collaborators");
    } finally {
      setUpdatingFor(null);
    }
  };

  if (isPending) {
    return (
      <div className="flex justify-center py-10 text-mediumgray">
        Loading subscribers...
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Both sections read the same filtered list — a filter that applied to
          only one of them would hide subscribers rather than scope them. */}
      <div className="flex flex-wrap items-end gap-4">
        <OrganizationFilter
          value={organizationId}
          onChange={setOrganizationId}
        />
        <div className="flex-1 min-w-64">
          <label className="block text-sm mb-1 font-medium">Search</label>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Name, email or Instagram username..."
            className="form-input border border-mediumgray w-full"
          />
        </div>
      </div>

      <section>
        <h2 className="text-lg font-semibold mb-3">
          Active Subscribers ({activeSubscribers.length})
        </h2>
        {activeSubscribers.length === 0 ? (
          <p className="text-mediumgray italic text-sm">No active subscribers.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeSubscribers.map((sub) => (
              <SubscriberCard
                key={sub.subscriptionId ?? sub.userId.toString()}
                subscriber={sub}
                allUsers={allUsers}
                onSendReminder={handleSendReminder}
                onUpdateCollaborators={handleUpdateCollaborators}
                sendingFor={sendingFor}
                updatingFor={updatingFor}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3 text-red-600">
          Expired / Inactive ({expiredSubscribers.length})
        </h2>
        {expiredSubscribers.length === 0 ? (
          <p className="text-mediumgray italic text-sm">None.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {expiredSubscribers.map((sub) => (
              <SubscriberCard
                key={sub.subscriptionId ?? sub.userId.toString()}
                subscriber={sub}
                allUsers={allUsers}
                onSendReminder={handleSendReminder}
                onUpdateCollaborators={handleUpdateCollaborators}
                sendingFor={sendingFor}
                updatingFor={updatingFor}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default SocialSubscribers;
